// src/pages/DriftDashboardPage.tsx — Nexus-themed drift table
// Replaces admin/src/pages/DriftDashboardPage.tsx
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import type { DriftReport, DriftRow } from '@eotir/oidcbridge-shared/api-contracts';

const statusChipClass: Record<DriftRow['status'], string> = {
  match: 'eo-chip eo-chip--ok',
  'kv-only': 'eo-chip eo-chip--warn',
  'config-only': 'eo-chip eo-chip--ops',
  'field-mismatch': 'eo-chip eo-chip--err',
  corrupt: 'eo-chip eo-chip--err',
};

const statusLabels: Record<DriftRow['status'], string> = {
  match: 'Match',
  'kv-only': 'KV only',
  'config-only': 'Config only',
  'field-mismatch': 'Field mismatch',
  corrupt: 'Corrupt',
};

export function DriftDashboardPage() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['drift'],
    queryFn: ({ signal }) => api<DriftReport>('/api/drift', { signal }),
  });

  const counts = (data?.rows ?? []).reduce<Partial<Record<DriftRow['status'], number>>>((acc, r) => {
    acc[r.status] = (acc[r.status] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div>
      <div className="eo-eyebrow">Configuration Drift Monitor</div>
      <div className="flex items-center justify-between mb-5">
        <h1 className="eo-h1">Drift</h1>
        <button type="button" onClick={() => refetch()} className="eo-btn eo-btn--ghost">⟳ Refresh</button>
      </div>

      {data && (
        <div className="grid grid-cols-4 gap-2.5 mb-6">
          <div className="eo-kpi"><div className="eo-kpi__n">{counts.match || 0}</div><div className="eo-kpi__l text-ok-soft">Match</div></div>
          <div className="eo-kpi"><div className="eo-kpi__n">{counts['kv-only'] || 0}</div><div className="eo-kpi__l text-warn-soft">KV Only</div></div>
          <div className="eo-kpi"><div className="eo-kpi__n">{counts['config-only'] || 0}</div><div className="eo-kpi__l text-info-soft">Config Only</div></div>
          <div className="eo-kpi"><div className="eo-kpi__n">{(counts['field-mismatch'] || 0) + (counts.corrupt || 0)}</div><div className="eo-kpi__l text-err-soft">Mismatch</div></div>
        </div>
      )}

      {isLoading && <p className="text-sm text-ink-3">Loading drift report…</p>}
      {error && <p className="text-sm text-err">Failed to load: {(error as Error).message}</p>}

      {data && (
        <>
          <p className="eo-hint mb-3" style={{marginTop:0}}>
            As of {new Date(data.asOf).toLocaleString()} · Source: {data.configSource}
          </p>
          {data.rows.length === 0 ? (
            <p className="text-sm text-ink-3">No drift — KV matches config exactly.</p>
          ) : (
            <table className="eo-table">
              <thead>
                <tr>
                  <th>Client ID</th>
                  <th>Status</th>
                  <th>Details</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.clientId}>
                    <td><span className="eo-mono">{r.clientId}</span></td>
                    <td>
                      <span className={statusChipClass[r.status]}>{statusLabels[r.status]}</span>
                    </td>
                    <td className="text-[12px] italic text-ink-3" style={{fontFamily:'Georgia, serif'}}>
                      {r.corruptionReason && <p className="text-err-soft">{r.corruptionReason}</p>}
                      {r.fieldDiffs && r.fieldDiffs.length > 0 && (
                        <ul className="space-y-0.5">
                          {r.fieldDiffs.map((d) => (
                            <li key={d.field}>
                              <span className="font-mono not-italic text-ink-2">{d.field}</span>: KV
                              <code className="mx-1 px-1 bg-bg-3 rounded text-ink not-italic">{JSON.stringify(d.kvValue)}</code>
                              vs config
                              <code className="mx-1 px-1 bg-bg-3 rounded text-ink not-italic">{JSON.stringify(d.configValue)}</code>
                            </li>
                          ))}
                        </ul>
                      )}
                      {!r.corruptionReason && (!r.fieldDiffs || r.fieldDiffs.length === 0) && (
                        <span className="text-ink-4 not-italic">—</span>
                      )}
                    </td>
                    <td className="text-right">
                      <Link to={`/clients/${r.clientId}`}
                            className="font-mono text-[11px] tracking-label uppercase text-crimson-hover no-underline hover:text-white">
                        View →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}
